<script setup lang="ts">
import type { GlobalTheme, NLocale } from 'naive-ui'
import enUS from 'naive-ui/es/locales/common/enUS'
import { computed, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { dashboardDarkTheme, dashboardTheme } from '~/design/themes/dashboard'
import { marketplaceTheme } from '~/design/themes/marketplace'
import { useUiStore } from '~/stores/ui'
import AppNotices from './AppNotices.vue'
import OfflineBanner from './OfflineBanner.vue'

/**
 * The theme is chosen by the route's layout, so the two design languages can never be mixed
 * on one screen (CLAUDE.md rule 6).
 */
const ui = useUiStore()
const route = useRoute()

const isMarketplace = computed(() => route.meta.layout === 'marketplace')

const themeOverrides = computed(() => {
  if (isMarketplace.value) return marketplaceTheme
  // `appliedDark`, not `isDark`: Naive turns with the page, not at the click (stores/ui.ts).
  const dashboard = ui.appliedDark ? dashboardDarkTheme : dashboardTheme
  if (route.meta.layout === 'dashboard') return dashboard
  return {
    ...dashboard,
    common: { ...dashboard.common, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' },
  }
})

/**
 * Naive's stock placeholders ("Please Input", "Please Select", "Select Date") are not words a
 * public page uses (`space-request.spec.ts` pinned one form; on 2026-10-29 the enquiry and
 * checkout forms still showed them). On the marketplace a field with no placeholder of its own
 * shows none -- its label asks the question, and a date field's calendar icon says how to answer;
 * the workspaces keep Naive's defaults.
 */
const DATE_PLACEHOLDERS = Object.keys(enUS.DatePicker).filter((key) => key.endsWith('Placeholder'))
const publicLocale: NLocale = {
  ...enUS,
  Input: { placeholder: '' },
  InputNumber: { placeholder: '' },
  Select: { placeholder: '' },
  DatePicker: {
    ...enUS.DatePicker,
    ...Object.fromEntries(DATE_PLACEHOLDERS.map((key) => [key, ''])),
  },
}

// The marketplace is a public, always-light surface — a dark public site is not the
// reference's language.
const darkTheme = shallowRef<GlobalTheme | null>(null)
// The public marketplace is always light. Fetch the full library's dark palette only when
// a workspace or authentication screen actually requests it.
//
// `naive-ui/es/themes/dark` exports `darkTheme` as a NAMED export and has no default. Reading
// `.default` here silently yielded `undefined`, so `n-config-provider` kept the LIGHT palette
// and only the handful of surfaces named in `dashboardDarkTheme` went dark — which is why every
// Naive-owned colour (body text, table headers, inputs, dividers, popovers, scrollbars) stayed
// light on a dark page. Named import, and a test pins it (dark-theme.spec.ts).
watch(
  [isMarketplace, () => ui.isDark],
  async ([marketplace, dark]) => {
    if (!marketplace && dark && !darkTheme.value) {
      darkTheme.value = (await import('naive-ui/es/themes/dark')).darkTheme
    }
  },
  { immediate: true },
)
// The palette is fetched as soon as dark is ASKED for (the watch above), so it is in hand by the
// time the page turns 800ms later; it is APPLIED with the page.
const theme = computed(() => (!isMarketplace.value && ui.appliedDark ? darkTheme.value : null))
</script>

<template>
  <n-config-provider :theme="theme" :theme-overrides="themeOverrides" :locale="isMarketplace ? publicLocale : undefined" inline-theme-disabled>
    <n-loading-bar-provider>
      <n-dialog-provider>
        <n-notification-provider>
          <n-message-provider>
            <AppNotices />
            <OfflineBanner />
            <slot />
          </n-message-provider>
        </n-notification-provider>
      </n-dialog-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>
